import type { CreditCard } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import BarcodeScanner from "~icons/material-symbols/barcode-scanner";
import { css, cx } from "../../../styled-system/css";
import amexIcon from "../../assets/card-brands/amex.png";
import dinersIcon from "../../assets/card-brands/diners.png";
import jcbIcon from "../../assets/card-brands/jcb.png";
import mastercardIcon from "../../assets/card-brands/mastercard.png";
import visaIcon from "../../assets/card-brands/visa.png";
import { t } from "../../libs/i18n";
import { formatNumber } from "../../libs/utils";

export const creditCardRegistrationLimit = 4;
export const creditCardRegistrationLimitMessage = t(
  "登録できるクレジットカードは4枚までです。新しいカードを登録する場合は、まず登録済みのカードを削除してください。",
);

type RadioCircleProps = {
  selected: boolean;
};

export function RadioCircle({ selected }: RadioCircleProps) {
  return (
    <span
      className={css({
        w: "20px",
        h: "20px",
        rounded: "full",
        border: selected ? "6px solid" : "1px solid",
        borderColor: selected ? "surface.accentPrimary" : "border.primary",
        bg: "surface.primary",
        flexShrink: 0,
      })}
    />
  );
}

type AmountOptionProps = {
  amount: 5000 | 10000;
  selected: boolean;
  onClick: () => void;
};

export function AmountOption({ amount, selected, onClick }: AmountOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={css({
        flex: "1",
        minH: "36px",
        bg: selected ? "surface.accentPrimary" : "#F8FAFC",
        border: "1px solid",
        borderColor: selected ? "surface.accentPrimary" : "border.secondary",
        color: selected ? "text.inverse" : "text.primary",
        rounded: "8px",
        px: "12px",
        py: "8px",
        fontSize: "14px",
        lineHeight: "1.4",
        fontWeight: "600",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
      })}
    >
      {t("{amount}円", { amount: formatNumber(amount) })}
    </button>
  );
}

type ChargeMethodOptionProps = {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
};

export function ChargeMethodOption({
  selected,
  title,
  description,
  onClick,
  disabled = false,
}: ChargeMethodOptionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        css({
          bg: "surface.primary",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          px: "20px",
          py: "12px",
          minH: "66px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          textAlign: "left",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
          _disabled: {
            opacity: 0.45,
            cursor: "not-allowed",
          },
        }),
        selected &&
          css({
            border: "2px solid",
            borderColor: "border.accentPrimary",
          }),
      )}
    >
      <RadioCircle selected={selected} />
      <span
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          minW: 0,
        })}
      >
        <span
          className={css({
            fontSize: "16px",
            lineHeight: "1.5",
            color: "text.primary",
          })}
        >
          {title}
        </span>
        <span
          className={css({
            fontSize: "14px",
            lineHeight: "1.4",
            color: "text.tertiary",
          })}
        >
          {description}
        </span>
      </span>
    </button>
  );
}

type AwaitingMypaymentOrderCardProps = {
  amount: number;
  availableUntilLabel: string;
  onClick: () => void;
};

export function AwaitingMypaymentOrderCard({
  amount,
  availableUntilLabel,
  onClick,
}: AwaitingMypaymentOrderCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={css({
        w: "full",
        bg: "surface.primary",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "12px",
        minH: "66px",
        px: "20px",
        py: "12px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        textAlign: "left",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
      })}
    >
      <BarcodeScanner
        className={css({ color: "text.secondary", flexShrink: 0 })}
        fontSize={28}
      />
      <div
        className={css({
          minW: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "4px",
        })}
      >
        <span
          className={css({
            bg: "surface.accentSuccessLight",
            color: "text.accentSuccess",
            rounded: "full",
            px: "12px",
            py: "2px",
            display: "flex",
            alignItems: "baseline",
            gap: "4px",
            whiteSpace: "nowrap",
          })}
        >
          <span className={css({ fontSize: "14px", lineHeight: "1.4" })}>
            {formatNumber(amount)}
          </span>
          <span
            className={css({
              fontSize: "12px",
              lineHeight: "1.35",
              fontWeight: "600",
            })}
          >
            {t("円")}
          </span>
        </span>
        <span
          className={css({
            color: "text.tertiary",
            fontSize: "12px",
            lineHeight: "1.3",
            whiteSpace: "nowrap",
          })}
        >
          {availableUntilLabel ? `${t("有効期限")} ${availableUntilLabel}` : ""}
        </span>
      </div>
      <ArrowForward
        className={css({ flexShrink: 0, color: "text.secondary" })}
        fontSize={18}
      />
    </button>
  );
}

type CreditCardRowProps = {
  card: CreditCard;
  selected: boolean;
  isDefault?: boolean;
  onClick: () => void;
};

export function CreditCardRow({
  card,
  selected,
  isDefault,
  onClick,
}: CreditCardRowProps) {
  const expiry = `${card.expiryMonth}/${card.expiryYear}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        css({
          w: "full",
          bg: "surface.primary",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          minH: "66px",
          px: "20px",
          py: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          textAlign: "left",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
        }),
        selected &&
          css({
            border: "2px solid",
            borderColor: "border.accentPrimary",
          }),
      )}
    >
      <RadioCircle selected={selected} />
      <CardBrand brand={card.cardBrand} />
      <div
        className={css({
          minW: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        })}
      >
        <div
          className={css({
            color: "text.primary",
            fontSize: "16px",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {formatMaskedCardNo(card.maskedCardNo)}
        </div>
        <div
          className={css({
            display: "flex",
            gap: "8px",
            color: "text.tertiary",
            fontSize: "12px",
            lineHeight: "1.3",
            whiteSpace: "nowrap",
          })}
        >
          <span>{expiry}</span>
          {isDefault && <span>{t("メインカード")}</span>}
        </div>
      </div>
    </button>
  );
}

type NewCardRowProps = {
  selected: boolean;
  onClick: () => void;
};

export function NewCardRow({ selected, onClick }: NewCardRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        css({
          w: "full",
          bg: "surface.primary",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          minH: "66px",
          px: "20px",
          py: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          textAlign: "left",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
        }),
        selected &&
          css({
            border: "2px solid",
            borderColor: "border.accentPrimary",
          }),
      )}
    >
      <RadioCircle selected={selected} />
      <span
        className={css({
          fontSize: "16px",
          lineHeight: "1.5",
          color: "text.primary",
        })}
      >
        {t("新しいカードを登録")}
      </span>
    </button>
  );
}

type AddCreditCardButtonProps = {
  disabled: boolean;
  onClick: () => void;
  variant?: "primary" | "secondary";
};

export function AddCreditCardButton({
  disabled,
  onClick,
  variant = "secondary",
}: AddCreditCardButtonProps) {
  const limitMessageId = "credit-card-registration-limit-message";
  const isPrimary = variant === "primary";

  return (
    <div
      className={css({
        w: "full",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      })}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-describedby={disabled ? limitMessageId : undefined}
        className={css({
          w: "full",
          minH: "54px",
          bg: isPrimary ? "surface.accentPrimary" : "surface.primary",
          border: "1px solid",
          borderColor: isPrimary ? "surface.accentPrimary" : "border.secondary",
          rounded: "12px",
          px: "20px",
          py: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          color: isPrimary ? "text.inverse" : "text.primary",
          fontSize: "16px",
          lineHeight: "1.5",
          fontWeight: "600",
          textAlign: "left",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
          _disabled: {
            bg: "#F8FAFC",
            color: "text.disable",
            cursor: "not-allowed",
          },
        })}
      >
        {t("新しいカードを登録")}
        <ArrowForward
          className={css({
            flexShrink: 0,
            color: disabled
              ? "text.disable"
              : isPrimary
                ? "text.inverse"
                : "text.secondary",
          })}
          fontSize={18}
        />
      </button>
      {disabled ? (
        <p
          id={limitMessageId}
          className={css({
            color: "text.secondary",
            fontSize: "13px",
            lineHeight: "1.45",
          })}
        >
          {creditCardRegistrationLimitMessage}
        </p>
      ) : null}
    </div>
  );
}

type CreditCardManageRowProps = {
  card: CreditCard;
  isDefault?: boolean;
  showDivider?: boolean;
  onClick: () => void;
};

export function CreditCardManageRow({
  card,
  isDefault,
  showDivider,
  onClick,
}: CreditCardManageRowProps) {
  const expiry = `${card.expiryMonth}/${card.expiryYear}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={css({
        w: "full",
        bg: "surface.primary",
        minH: "72px",
        px: "20px",
        py: "12px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        textAlign: "left",
        borderBottom: showDivider ? "1px solid" : undefined,
        borderColor: showDivider ? "border.secondary" : undefined,
      })}
    >
      <CardBrand brand={card.cardBrand} />
      <div
        className={css({
          minW: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        })}
      >
        <div
          className={css({
            color: "text.primary",
            fontSize: "16px",
            lineHeight: "1.5",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {formatMaskedCardNo(card.maskedCardNo)}
        </div>
        <div
          className={css({
            display: "flex",
            gap: "8px",
            color: "text.tertiary",
            fontSize: "12px",
            lineHeight: "1.3",
            whiteSpace: "nowrap",
          })}
        >
          <span>{expiry}</span>
          {isDefault && <span>{t("メインカード")}</span>}
        </div>
      </div>
      <ArrowForward
        className={css({ flexShrink: 0, color: "text.secondary" })}
        fontSize={18}
      />
    </button>
  );
}

export function CardBrand({ brand }: { brand: string }) {
  const brandInfo = getBrandInfo(brand);

  if (!brandInfo) {
    const label = brandLabel(brand);

    return (
      <span
        className={css({
          w: "42px",
          h: "30px",
          rounded: "4px",
          border: "1px solid",
          borderColor: "border.secondary",
          bg: "surface.primary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "text.primary",
          fontSize: label.length > 4 ? "9px" : "12px",
          fontWeight: "700",
        })}
        aria-label={label}
      >
        {label}
      </span>
    );
  }

  return (
    <img
      src={brandInfo.src}
      alt={brandInfo.label}
      className={css({
        w: brandInfo.width,
        h: "32px",
        objectFit: "contain",
        flexShrink: 0,
      })}
    />
  );
}

function getBrandInfo(brand: string) {
  const normalized = brand.toLowerCase();
  if (normalized.includes("visa")) {
    return { label: "VISA", src: visaIcon, width: "56px" };
  }
  if (normalized.includes("master") || normalized === "mc") {
    return { label: "Mastercard", src: mastercardIcon, width: "45px" };
  }
  if (normalized.includes("jcb")) {
    return { label: "JCB", src: jcbIcon, width: "42px" };
  }
  if (normalized.includes("amex") || normalized.includes("american")) {
    return { label: "American Express", src: amexIcon, width: "34px" };
  }
  if (normalized.includes("diners")) {
    return { label: "Diners Club", src: dinersIcon, width: "44px" };
  }
  return null;
}

function brandLabel(brand: string) {
  const normalized = brand.toLowerCase();
  if (normalized.includes("visa")) {
    return "VISA";
  }
  if (normalized.includes("master")) {
    return "MC";
  }
  if (normalized.includes("jcb")) {
    return "JCB";
  }
  if (normalized.includes("amex") || normalized.includes("american")) {
    return "AMEX";
  }
  if (normalized.includes("diners")) {
    return "Diners";
  }
  return "CARD";
}

export function formatMaskedCardNo(maskedCardNo: string) {
  const last4 = maskedCardNo.match(/(\d{4})$/)?.[1];
  if (last4) {
    return `xxxx-xxxx-xxxx-${last4}`;
  }
  return maskedCardNo;
}
